import { useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import { revokeAuthorization } from './nodeClaimsApi';
import type { NodeClaimAuthorization } from './types';

/**
 * Revokes the AUTHORIZATION, not any key already issued from it.
 *
 * That distinction is the whole reason this is a dialog and not a one-click DeleteButton:
 * an operator who reads "revoke" as "this node is now locked out" will walk away from a node
 * that is still online and still authenticating. RevokeNodeClaimCommandHandler does not touch
 * a DeviceApiKey, so the dialog says so before the operator commits.
 *
 * Reason is mandatory server-side and is the audit trail, so it gates the confirm button.
 */
export const RevokeAuthorizationButton = ({ record }: { record?: NodeClaimAuthorization }) => {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record || record.status === 'Revoked') return null;

    const close = () => {
        if (busy) return;
        setOpen(false);
        setReason('');
    };

    const confirm = async () => {
        setBusy(true);
        try {
            await revokeAuthorization(record.id, reason.trim());
            notify(`Authorization for ${record.hardwareSerial} revoked`, { type: 'success' });
            setOpen(false);
            setReason('');
            refresh();
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Revoke failed', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button
                size="small"
                color="error"
                startIcon={<BlockIcon />}
                onClick={(event) => { event.stopPropagation(); setOpen(true); }}
            >
                Revoke
            </Button>

            <Dialog open={open} onClose={close} maxWidth="sm" fullWidth onClick={e => e.stopPropagation()}>
                <DialogTitle>Revoke authorization for {record.hardwareSerial}?</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div" sx={{ mb: 2 }}>
                        This stops serial <strong>{record.hardwareSerial}</strong> from claiming node{' '}
                        <strong>{record.nodeId}</strong> in future.
                        {record.status === 'Claimed' && (
                            <>
                                {' '}
                                It does <strong>not</strong> revoke the API key already issued from this
                                authorization. That node stays online and keeps working. To take its access
                                away, revoke its key under Provisioned Devices as well.
                            </>
                        )}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        required
                        label="Reason"
                        helperText="Recorded against your identity in the audit trail."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} disabled={busy}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirm}
                        disabled={busy || reason.trim().length === 0}
                    >
                        Revoke
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
